import { Redirect } from "@redotech/react-router-util/redirect";
import { PermissionedRouteObject } from "@redotech/redo-merchant-app-common/routing/protected-routes";
import { ACTIVE_CONVERSATION_QUERY_PARAMETER } from "@redotech/redo-merchant-support-app/query-parameters";
import { Support } from "@redotech/redo-merchant-support-app/support";
import { SupportPermissions } from "@redotech/redo-model/permissions";
import { matchPath } from "react-router-dom";
import { conciergeRoutes } from "../../../concierge/route";
import { devRoutes } from "../../../support-dev/route";
import { Page } from "../../page";
import { faqRoutes } from "./faq-routes";
import { SupportV2RedirectGuard } from "./support-v2-redirect-guard";

const HIDE_PADDING_ROUTES: string[] = [
  "/table/*",
  "/concierge/conversations",
  "/about",
  "/faq/*",
  "/dev/conversation-insights",
];

function hidePadding(url: string): boolean {
  return HIDE_PADDING_ROUTES.some(
    (route) => !!matchPath(`/stores/:storeId/support${route}`, url),
  );
}

const FULLSCREEN_ROUTES: string[] = [
  "/concierge/onboard",
  "/setup",
  "/faq/editor/*",
];

const HIDE_HEADER_ROUTES: string[] = [...FULLSCREEN_ROUTES];
function hideHeader(url: string, { search }: { search: string }): boolean {
  if (search.includes(ACTIVE_CONVERSATION_QUERY_PARAMETER)) {
    return true;
  }

  return HIDE_HEADER_ROUTES.some(
    (route) => !!matchPath(`/stores/:storeId/support${route}`, url),
  );
}

const HIDE_NAV_ROUTES: string[] = [...FULLSCREEN_ROUTES];

function hideNav(url: string): boolean {
  return HIDE_NAV_ROUTES.some(
    (route) => !!matchPath(`/stores/:storeId/support${route}`, url),
  );
}

export const SUPPORT_VIEW_NAME = "Ticket view";

export const supportRoutes: PermissionedRouteObject[] = [
  {
    handle: { breadcrumb: "Support", breadcrumbNavigate: false },
    async lazy() {
      const { Page } = await import("../../page");
      return {
        element: (
          <Page
            hideHeader={hideHeader}
            hideNavbar={hideNav}
            hidePadding={hidePadding}
            newChrome
          />
        ),
      };
    },
    children: [
      {
        path: "about",
        async lazy() {
          const { AboutSupport } = await import(
            "@redotech/redo-merchant-support-app/about-support"
          );
          return { element: <AboutSupport /> };
        },
      },
      {
        path: "setup",
        async lazy() {
          const { StartSupportTrial } = await import(
            "@redotech/redo-merchant-support-app/support-setup/start-support-trial"
          );
          return { element: <StartSupportTrial /> };
        },
      },
      { path: "faq", children: faqRoutes },
      {
        path: "dashboard",
        handle: {
          breadcrumb: "Dashboard",
          permission: SupportPermissions.VIEW_ANALYTICS,
        },
        async lazy() {
          const { ConciergeDashboardPage } = await import(
            "@redotech/redo-merchant-support-app/analytics/dashboards/concierge-dashboard-page"
          );
          return { Component: ConciergeDashboardPage };
        },
      },
      { path: "concierge", children: conciergeRoutes },
      { path: "dev", children: devRoutes },
      {
        path: "llm-thread/:llmThreadId",
        handle: { breadcrumb: "LLM Thread" },
        async lazy() {
          const { LlmThreadPage } = await import(
            "@redotech/redo-merchant-support-app/llm-thread/llm-thread-page"
          );
          return { Component: LlmThreadPage };
        },
      },
      {
        handle: { breadcrumb: SUPPORT_VIEW_NAME },
        path: "*",
        element: (
          <SupportV2RedirectGuard>
            <Support />
          </SupportV2RedirectGuard>
        ),
      },
    ],
  },
];

export const backwardsCompatibleSupportRoutes: PermissionedRouteObject[] = [
  {
    path: "concierge",
    children: [
      {
        element: <Page />,
        children: [
          {
            index: true,
            element: (
              <Redirect match={/concierge/} replaceValue="support/concierge" />
            ),
          },
          {
            path: "*",
            element: (
              <Redirect match={/concierge/} replaceValue="support/concierge" />
            ),
          },
        ],
      },
    ],
  },
];
